import { useEffect, useState } from 'react';
import type { DataTableColumn, DataTableStateChangeEvent } from '../DataTable.model';
import { DataTableEvent } from '../DataTable.model';
import { useDataTable } from '../DataTableProvider';

/**
 * Hook that returns reactive columns array that updates when columns change.
 * Subscribes to TableStateChange events to trigger re-renders when columns are added/removed/updated.
 *
 * @returns Array of DataTable columns
 */
export const useColumns = (): ReadonlyArray<DataTableColumn> => {
  const { dataTableService } = useDataTable();
  const [columns, setColumns] = useState<ReadonlyArray<DataTableColumn>>(() => dataTableService.columns);

  useEffect(() => {
    const abortController = new AbortController();

    const stateChangeListener = (event: DataTableStateChangeEvent) => {
      if (event.detail.type.has('columns')) {
        setColumns(dataTableService.columns);
      }
    };

    dataTableService.subscribe(DataTableEvent.TableStateChange, stateChangeListener, abortController.signal);

    return () => {
      abortController.abort();
    };
  }, [dataTableService]);

  return columns;
};

/**
 * Hook that returns reactive leaf columns array that updates when columns change.
 * Leaf columns are renderable columns only (excludes parent columns from column grouping).
 * Subscribes to TableStateChange events to trigger re-renders when columns are added/removed/updated.
 *
 * @returns Array of DataTable leaf columns (renderable columns only)
 */
export const useLeafColumns = (): ReadonlyArray<DataTableColumn> => {
  const { dataTableService } = useDataTable();
  const [leafColumns, setLeafColumns] = useState<ReadonlyArray<DataTableColumn>>(() => dataTableService.leafColumns);

  useEffect(() => {
    const abortController = new AbortController();

    const stateChangeListener = (event: DataTableStateChangeEvent) => {
      if (event.detail.type.has('columns')) {
        setLeafColumns(dataTableService.leafColumns);
      }
    };

    dataTableService.subscribe(DataTableEvent.TableStateChange, stateChangeListener, abortController.signal);

    return () => {
      abortController.abort();
    };
  }, [dataTableService]);

  return leafColumns;
};
